import type { ReactNode } from "react";

/** Semantic colour roles used across every component in the app. */
export type Tone =
  | "default"
  | "muted"
  | "accent"
  | "money"
  | "business"
  | "ok"
  | "warn"
  | "danger"
  | "info";

const DOT_BG: Record<Tone, string> = {
  default: "bg-fg",
  muted: "bg-muted",
  accent: "bg-accent",
  money: "bg-money",
  business: "bg-business",
  ok: "bg-ok",
  warn: "bg-warn",
  danger: "bg-danger",
  info: "bg-info",
};

export const TONE_TEXT: Record<Tone, string> = {
  default: "text-fg",
  muted: "text-muted",
  accent: "text-accent",
  money: "text-money",
  business: "text-business",
  ok: "text-ok",
  warn: "text-warn",
  danger: "text-danger",
  info: "text-info",
};

interface StatusDotProps {
  tone?: Tone;
  label?: ReactNode;
  /** Soft pulse for "live" things (health polling, streaming answer). */
  pulse?: boolean;
  title?: string;
  className?: string;
}

/** Small coloured dot, optionally with a label. The app's status vocabulary. */
export default function StatusDot({
  tone = "muted",
  label,
  pulse = false,
  title,
  className = "",
}: StatusDotProps) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-xs ${className}`}
      title={title}
    >
      <span className="relative grid size-2 place-items-center">
        {pulse && (
          <span
            className={`absolute inline-flex size-2 animate-ping rounded-full opacity-60 ${DOT_BG[tone]}`}
          />
        )}
        <span className={`relative inline-flex size-2 rounded-full ${DOT_BG[tone]}`} />
      </span>
      {label !== undefined && <span className="text-muted">{label}</span>}
    </span>
  );
}

/** Pill badge sharing the same tone vocabulary. */
export function Badge({
  tone = "muted",
  children,
  title,
  className = "",
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
  className?: string;
}) {
  const ring: Record<Tone, string> = {
    default: "bg-surface-2 text-fg",
    muted: "bg-surface-2 text-muted",
    accent: "bg-accent/15 text-accent",
    money: "bg-money/15 text-money",
    business: "bg-business/15 text-business",
    ok: "bg-ok/15 text-ok",
    warn: "bg-warn/15 text-warn",
    danger: "bg-danger/15 text-danger",
    info: "bg-info/15 text-info",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-control px-1.5 py-0.5 text-micro font-medium whitespace-nowrap ${ring[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Map a backend Severity onto a tone.
 *
 * `info` gets the neutral grey-blue, not the accent: the accent is the app's
 * "this is active / this is the primary action" colour, and spending it on the
 * most common severity made every routine insight look urgent.
 */
export function severityTone(severity: "info" | "warn" | "critical"): Tone {
  if (severity === "critical") return "danger";
  if (severity === "warn") return "warn";
  return "info";
}

/**
 * The same severity in words.
 *
 * A coloured dot is the whole signal on the busiest rows of the home screen,
 * which leaves a colour-blind reader with nothing at all. Passing this as the
 * dot's `title` gives the mark a name without adding a word to every row and
 * breaking the few-second scan those rows exist for.
 */
export function severityLabel(severity: "info" | "warn" | "critical"): string {
  if (severity === "critical") return "критично";
  if (severity === "warn") return "потребує уваги";
  return "до відома";
}
